package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.components.LabelSelector
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.files.File
import org.w3c.files.get

private enum class ImportStep { SELECT_FILE, PREVIEW, IMPORTING, RESULTS }

@Composable
fun AudiobookImportForm(
    apiClient: ApiClient,
    labels: List<LabelDto> = emptyList(),
    userLabelId: Int? = null,
    isLabelEditor: Boolean = false,
    onImportComplete: (ImportReportDto) -> Unit = {},
    onCancel: (() -> Unit)? = null
) {
    val colors = AppTheme.colors
    var step by remember { mutableStateOf(ImportStep.SELECT_FILE) }
    var selectedFile by remember { mutableStateOf<File?>(null) }
    var selectedLabelId by remember { mutableStateOf<Int?>(if (isLabelEditor) userLabelId else null) }
    var isLoading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var preview by remember { mutableStateOf<CsvPreviewResponse?>(null) }
    var importReport by remember { mutableStateOf<ImportReportDto?>(null) }

    val scope = rememberCoroutineScope()

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            themeTransition()
        }
    }) {
        H2({
            style {
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
                marginTop(0.px)
                marginBottom(Spacing.lg)
            }
        }) {
            Text("Import Audiobooks from CSV")
        }

        when (step) {
            // ── Step 1: File Selection ────────────────────────────────
            ImportStep.SELECT_FILE -> {
                P({
                    style { fontSize(Typography.base); color(Color(colors.textMuted)); marginBottom(Spacing.lg) }
                }) {
                    Text("Upload a CSV file with audiobook metadata. Required columns: ISBN, Title, Author.")
                }

                // Label selector
                if (labels.isNotEmpty()) {
                    Div({ style { marginBottom(Spacing.lg) } }) {
                        LabelSelector(
                            labels = labels,
                            selectedLabelId = selectedLabelId,
                            onLabelSelected = { selectedLabelId = it },
                            readOnly = isLabelEditor,
                            userLabelId = userLabelId
                        )
                    }
                }

                // File input
                Div({
                    style { marginBottom(Spacing.md) }
                }) {
                    Span({ style { fontSize(12.px); color(Color(colors.textMuted)); fontWeight("500"); display(DisplayStyle.Block); marginBottom(4.px) } }) { Text("CSV File") }
                    Input(InputType.File) {
                        attr("accept", ".csv")
                        onInput { event ->
                            val files = (event.target as? org.w3c.dom.HTMLInputElement)?.files
                            selectedFile = files?.get(0)
                            errorMessage = null
                        }
                        style {
                            padding(Spacing.sm)
                            fontSize(Typography.base)
                            width(100.percent)
                        }
                    }
                }

                if (selectedFile != null) {
                    Div({
                        style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginTop(Spacing.sm) }
                    }) {
                        val sizeKb = (selectedFile!!.size.toDouble() / 1024).toInt()
                        Text("Selected: ${selectedFile!!.name} (${sizeKb} KB)")
                    }
                }

                errorMessage?.let { ErrorBanner(it) }

                // Action buttons
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(Spacing.md)
                        marginTop(Spacing.xl)
                    }
                }) {
                    onCancel?.let {
                        Button({
                            onClick { it() }
                            style { csvSecondaryButtonStyle(colors) }
                        }) { Text("Cancel") }
                    }

                    Button({
                        if (selectedFile == null || isLoading) disabled()
                        onClick {
                            scope.launch {
                                isLoading = true
                                errorMessage = null
                                try {
                                    preview = apiClient.previewCsvImport(selectedFile!!)
                                    step = ImportStep.PREVIEW
                                } catch (e: Exception) {
                                    errorMessage = e.message ?: "Preview failed"
                                }
                                isLoading = false
                            }
                        }
                        style { csvPrimaryButtonStyle(colors, disabled = selectedFile == null || isLoading) }
                    }) { Text(if (isLoading) "Analyzing..." else "Preview Import") }
                }
            }

            // ── Step 2: Preview & Confirmation ───────────────────────
            ImportStep.PREVIEW -> {
                val p = preview!!

                // Encoding badge + row count
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.sm)
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) { Text("Encoding:") }
                    val isConverted = p.detectedEncoding != "UTF-8"
                    Span({
                        style {
                            fontSize(Typography.sm)
                            padding(2.px, Spacing.sm)
                            borderRadius(BorderRadius.sm)
                            backgroundColor(Color(if (isConverted) "#fef3c7" else "#dcfce7"))
                            color(Color(if (isConverted) "#92400e" else "#166534"))
                        }
                    }) {
                        Text(if (isConverted) "Converted from ${p.detectedEncoding}" else "UTF-8")
                    }
                    Span({ style { fontSize(Typography.sm); color(Color(colors.textMuted)); marginLeft(Spacing.lg) } }) {
                        Text("${p.totalRows} rows")
                    }
                }

                // Column mapping
                Div({
                    style {
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.md)
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Div({
                        style { fontWeight("600"); fontSize(Typography.sm); color(Color(colors.text)); marginBottom(Spacing.sm) }
                    }) { Text("Column Mapping") }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexWrap(FlexWrap.Wrap)
                            gap(Spacing.sm)
                        }
                    }) {
                        p.columnMapping.forEach { col ->
                            Span({
                                style {
                                    fontSize(11.px)
                                    padding(2.px, Spacing.sm)
                                    borderRadius(BorderRadius.sm)
                                    if (col.detected) {
                                        backgroundColor(Color("#dcfce7"))
                                        color(Color("#166534"))
                                    } else if (col.isRequired) {
                                        backgroundColor(Color("#fee2e2"))
                                        color(Color("#991b1b"))
                                    } else {
                                        backgroundColor(Color(colors.surfaceHover))
                                        color(Color(colors.textMuted))
                                        border(1.px, LineStyle.Solid, Color(colors.border))
                                    }
                                }
                            }) {
                                val icon = if (col.detected) "+" else if (col.isRequired) "!" else "-"
                                val suffix = if (col.detected && col.detectedAs != col.expectedColumn) " (as ${col.detectedAs})" else ""
                                Text("$icon ${col.expectedColumn}$suffix")
                            }
                        }
                    }
                }

                // Data preview table
                if (p.previewRows.isNotEmpty()) {
                    Div({
                        style { marginBottom(Spacing.lg); overflowX("auto") }
                    }) {
                        Div({
                            style { fontWeight("600"); fontSize(Typography.sm); color(Color(colors.text)); marginBottom(Spacing.sm) }
                        }) { Text("Preview (first ${p.previewRows.size} rows)") }

                        Table({
                            style {
                                width(100.percent)
                                fontSize(Typography.sm)
                                property("border-collapse", "collapse")
                            }
                        }) {
                            Thead {
                                Tr {
                                    listOf("Row", "ISBN", "Title", "Author", "Status").forEach { header ->
                                        Th({
                                            style {
                                                textAlign("left")
                                                padding(Spacing.sm)
                                                property("border-bottom", "2px solid ${colors.border}")
                                                color(Color(colors.textMuted))
                                                fontSize(11.px)
                                                property("text-transform", "uppercase")
                                            }
                                        }) { Text(header) }
                                    }
                                }
                            }
                            Tbody {
                                p.previewRows.forEach { row ->
                                    Tr {
                                        Td({ style { tableCellStyle(colors) } }) { Text("${row.rowNumber}") }
                                        Td({ style { tableCellStyle(colors) } }) { Text(row.isbn) }
                                        Td({ style { tableCellStyle(colors); maxWidth(250.px); property("text-overflow", "ellipsis"); overflow("hidden"); property("white-space", "nowrap") } }) { Text(row.title) }
                                        Td({ style { tableCellStyle(colors) } }) { Text(row.author) }
                                        Td({ style { tableCellStyle(colors) } }) {
                                            val (statusColor, statusText) = when (row.status) {
                                                "new" -> "#166534" to "New"
                                                "exists" -> "#92400e" to "Exists"
                                                "duplicate_in_file" -> "#991b1b" to "Duplicate"
                                                else -> colors.textMuted to row.status
                                            }
                                            Span({ style { color(Color(statusColor)); fontWeight("500") } }) { Text(statusText) }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // Summary badges
                Div({
                    style {
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.md)
                        marginBottom(Spacing.lg)
                        display(DisplayStyle.Flex)
                        gap(Spacing.xl)
                    }
                }) {
                    SummaryBadge("Will Create", "${p.summary.willCreate}", "#166534", "#dcfce7")
                    if (p.duplicateCheck.existingCount > 0) {
                        SummaryBadge("Already Exist", "${p.duplicateCheck.existingCount}", "#92400e", "#fef3c7")
                    }
                    if (p.summary.willFail > 0) {
                        SummaryBadge("Parse Errors", "${p.summary.willFail}", "#991b1b", "#fee2e2")
                    }
                }

                // Parse errors
                if (p.parseErrors.isNotEmpty()) {
                    Div({
                        style {
                            maxHeight(150.px); overflowY("auto"); marginBottom(Spacing.lg); padding(Spacing.md)
                            backgroundColor(Color(if (colors == LightColors) "#fef2f2" else "#450a0a"))
                            borderRadius(BorderRadius.md)
                        }
                    }) {
                        Div({ style { fontWeight("600"); fontSize(Typography.sm); color(Color(colors.danger)); marginBottom(Spacing.sm) } }) {
                            Text("Parse Errors")
                        }
                        p.parseErrors.forEach { err ->
                            Div({ style { fontSize(Typography.sm); color(Color(colors.danger)) } }) {
                                Text("Row ${err.row}: ${err.error}")
                            }
                        }
                    }
                }

                // Missing required columns
                val missingRequired = p.columnMapping.filter { it.isRequired && !it.detected }
                if (missingRequired.isNotEmpty()) {
                    ErrorBanner("Missing required columns: ${missingRequired.joinToString { it.expectedColumn }}. Import cannot proceed.")
                }

                errorMessage?.let { ErrorBanner(it) }

                // Buttons
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(Spacing.md)
                        marginTop(Spacing.lg)
                    }
                }) {
                    Button({
                        onClick { step = ImportStep.SELECT_FILE; preview = null; errorMessage = null }
                        style { csvSecondaryButtonStyle(colors) }
                    }) { Text("Back") }

                    Button({
                        val canImport = p.summary.willCreate > 0 && missingRequired.isEmpty()
                        if (!canImport || isLoading) disabled()
                        onClick {
                            step = ImportStep.IMPORTING
                            scope.launch {
                                isLoading = true; errorMessage = null
                                try {
                                    val report = apiClient.importAudiobooksFromCsv(selectedFile!!, labelId = selectedLabelId, skipExisting = true)
                                    importReport = report
                                    step = ImportStep.RESULTS
                                    onImportComplete(report)
                                } catch (e: Exception) {
                                    errorMessage = e.message ?: "Import failed"
                                    step = ImportStep.PREVIEW
                                }
                                isLoading = false
                            }
                        }
                        style { csvPrimaryButtonStyle(colors, disabled = p.summary.willCreate <= 0 || missingRequired.isNotEmpty()) }
                    }) { Text("Confirm Import (${p.summary.willCreate} audiobooks)") }
                }
            }

            // ── Step 3: Importing ────────────────────────────────────
            ImportStep.IMPORTING -> {
                Div({
                    style {
                        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center); padding(Spacing.xxl); gap(Spacing.lg)
                    }
                }) {
                    Div({
                        style {
                            width(40.px); height(40.px)
                            border(3.px, LineStyle.Solid, Color(colors.border))
                            property("border-top-color", colors.primary)
                            borderRadius(50.percent)
                            property("animation", "castly-spin 1s linear infinite")
                        }
                    })
                    Style { "@keyframes castly-spin { to { transform: rotate(360deg); } }" }
                    Div({ style { fontSize(Typography.lg); fontWeight("500"); color(Color(colors.text)) } }) {
                        Text("Importing ${preview?.summary?.willCreate ?: ""} audiobooks...")
                    }
                    Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                        Text("Please don't close this page.")
                    }
                }
            }

            // ── Step 4: Results + Error Download ─────────────────────
            ImportStep.RESULTS -> {
                val report = importReport!!

                val (statusBg, statusColor, statusText) = when (report.status) {
                    "SUCCESS" -> Triple("#dcfce7", "#166534", "Import Successful")
                    "PARTIAL" -> Triple("#fef3c7", "#92400e", "Import Partially Successful")
                    else -> Triple("#fee2e2", "#991b1b", "Import Failed")
                }
                Div({
                    style {
                        backgroundColor(Color(statusBg)); borderRadius(BorderRadius.md)
                        padding(Spacing.md); marginBottom(Spacing.lg)
                    }
                }) {
                    Div({ style { fontWeight("600"); color(Color(statusColor)); fontSize(Typography.base) } }) { Text(statusText) }
                }

                Div({
                    style { display(DisplayStyle.Flex); gap(Spacing.xl); marginBottom(Spacing.lg) }
                }) {
                    SummaryBadge("Total", "${report.totalRows}", colors.text, colors.surfaceHover)
                    SummaryBadge("Succeeded", "${report.successfulRows}", "#166534", "#dcfce7")
                    if (report.failedRows > 0) SummaryBadge("Failed", "${report.failedRows}", "#991b1b", "#fee2e2")
                }

                if (report.errors.isNotEmpty()) {
                    Div({
                        style {
                            maxHeight(200.px); overflowY("auto"); marginBottom(Spacing.lg); padding(Spacing.md)
                            backgroundColor(Color(if (colors == LightColors) "#fef2f2" else "#450a0a"))
                            borderRadius(BorderRadius.md)
                        }
                    }) {
                        report.errors.forEach { err ->
                            Div({ style { fontSize(Typography.sm); color(Color(colors.danger)); marginBottom(2.px) } }) {
                                Text("Row ${err.row ?: "-"}: ${err.error}")
                            }
                        }
                    }

                    Button({
                        onClick { downloadErrorCsv(report.errors) }
                        style {
                            padding(Spacing.sm, Spacing.md)
                            backgroundColor(Color("transparent"))
                            color(Color(colors.primary))
                            border(1.px, LineStyle.Solid, Color(colors.primary))
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            fontSize(13.px)
                            marginBottom(Spacing.lg)
                        }
                    }) { Text("Download Error Report (CSV)") }
                }

                Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd) } }) {
                    Button({
                        onClick {
                            step = ImportStep.SELECT_FILE
                            selectedFile = null; preview = null; importReport = null; errorMessage = null
                        }
                        style { csvPrimaryButtonStyle(colors) }
                    }) { Text("Close") }
                }
            }
        }
    }
}

@Composable
private fun SummaryBadge(label: String, value: String, textColor: String, bgColor: String) {
    Div({
        style {
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            padding(Spacing.sm, Spacing.md)
            backgroundColor(Color(bgColor)); borderRadius(BorderRadius.md); minWidth(80.px)
        }
    }) {
        Div({ style { fontSize(20.px); fontWeight("700"); color(Color(textColor)) } }) { Text(value) }
        Div({ style { fontSize(11.px); color(Color(textColor)); property("opacity", "0.7") } }) { Text(label) }
    }
}

@Composable
private fun ErrorBanner(message: String) {
    val colors = AppTheme.colors
    Div({
        style {
            backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
            border(1.px, LineStyle.Solid, Color(colors.danger))
            borderRadius(BorderRadius.md); padding(Spacing.md); marginTop(Spacing.md)
            fontSize(Typography.base); color(Color(colors.danger))
        }
    }) { Text(message) }
}

private fun StyleScope.csvPrimaryButtonStyle(colors: com.castly.admin.ui.theme.AppColors, disabled: Boolean = false) {
    padding(Spacing.sm, Spacing.lg); fontSize(Typography.base); fontWeight("500")
    color(if (disabled) Color(colors.textMuted) else Color("#ffffff"))
    backgroundColor(if (disabled) Color(colors.surfaceHover) else Color(colors.primary))
    border(0.px); borderRadius(BorderRadius.md)
    cursor(if (disabled) "not-allowed" else "pointer")
}

private fun StyleScope.csvSecondaryButtonStyle(colors: com.castly.admin.ui.theme.AppColors) {
    padding(Spacing.sm, Spacing.lg); fontSize(Typography.base); fontWeight("500")
    color(Color(colors.text)); backgroundColor(Color(colors.surface))
    border(1.px, LineStyle.Solid, Color(colors.border)); borderRadius(BorderRadius.md); cursor("pointer")
}

private fun StyleScope.tableCellStyle(colors: com.castly.admin.ui.theme.AppColors) {
    padding(Spacing.sm); property("border-bottom", "1px solid ${colors.border}"); color(Color(colors.text))
}

private fun downloadErrorCsv(errors: List<ImportErrorDto>) {
    val csvContent = buildString {
        appendLine("Row,Error")
        errors.forEach { e ->
            appendLine("${e.row ?: ""},\"${e.error.replace("\"", "\"\"")}\"")
        }
    }
    val blob = Blob(arrayOf(csvContent.asDynamic()), BlobPropertyBag(type = "text/csv;charset=utf-8"))
    val url = URL.createObjectURL(blob)
    val link = document.createElement("a") as HTMLAnchorElement
    link.href = url
    link.download = "import-errors.csv"
    document.body?.appendChild(link)
    link.click()
    document.body?.removeChild(link)
    URL.revokeObjectURL(url)
}
